import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useShiftBrief,
  logShiftBriefAcknowledgment,
} from '@/services/db/shiftBrief';
import { useAuthStore } from '@/store/authStore';

// Info-level hitch entries hide behind a "View all" disclosure when
// the info count exceeds this threshold. Warnings (>15 hitch days)
// are always shown inline. Demo data stays under this threshold,
// but the disclosure is coded now for real-world manifests.
const HITCH_WARNING_DISCLOSURE_THRESHOLD = 10;

// Zone 5 severity → Tailwind class mapping. Same /15 tint convention
// used by existing flags elsewhere in the Card (severe weather, SSE
// elevated, SIF forecast). Inline helper beats a new component for a
// single-file consumer; extract to a SeverityBadge if/when a second
// surface needs it.
function severityBadgeClass(severity: 'info' | 'warning' | 'critical'): string {
  switch (severity) {
    case 'critical': return 'bg-mozaic-red/15 text-mozaic-red';
    case 'warning':  return 'bg-mozaic-orange/15 text-mozaic-orange';
    case 'info':     return 'bg-mozaic-blue/15 text-mozaic-blue';
  }
}

interface Props {
  dsrId: string;
  tenantId: string;
  reportStatus: string;
  mode: 'entry' | 'detail';
}

export default function ShiftBriefCard({
  dsrId,
  tenantId,
  reportStatus,
  mode,
}: Props) {
  const { data, isPending, isError, refetch } = useShiftBrief(dsrId);
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  const [signalsOpen, setSignalsOpen] = useState(false);
  const [showAllHitchFlags, setShowAllHitchFlags] = useState(false);

  // Draft DSRs in entry mode don't log an ack — an SSR editing their
  // own draft isn't "viewing a brief." Every other case logs once,
  // first-viewer-wins via UNIQUE(dsr_report_id).
  const shouldLogAck =
    mode === 'detail' || (mode === 'entry' && reportStatus !== 'draft');

  useEffect(() => {
    if (!shouldLogAck) return;
    if (!userId) return;
    if (!data) return;
    logShiftBriefAcknowledgment({ dsrId, tenantId, userId }).catch(() => {
      // Services layer already swallows; double-swallow here so a
      // stray rejection can't crash the card.
    });
  }, [shouldLogAck, userId, data, dsrId, tenantId]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['shift_brief', dsrId] });
  };

  // ── Loading skeleton ──────────────────────────────────────

  if (isPending) {
    return (
      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="flex items-center justify-between">
            <div className="h-5 w-32 animate-pulse rounded bg-muted" />
            <div className="h-8 w-24 animate-pulse rounded bg-muted" />
          </div>
          <div className="space-y-2 border-t pt-3">
            <div className="h-20 animate-pulse rounded bg-muted" />
          </div>
          <div className="space-y-2 border-t pt-3">
            <div className="h-16 animate-pulse rounded bg-muted" />
          </div>
          <div className="space-y-2 border-t pt-3">
            <div className="h-16 animate-pulse rounded bg-muted" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Error state ───────────────────────────────────────────

  if (isError || !data) {
    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Couldn't load the Shift Brief.
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { zone1, zone2, zone3, zone4, zone5, signals, generatedAt } = data;

  const warningHitchFlags = zone2.hitchFlags.filter((h) => h.flag === 'warning');
  const infoHitchFlags = zone2.hitchFlags.filter((h) => h.flag === 'info');
  const shouldOfferHitchDisclosure =
    infoHitchFlags.length > HITCH_WARNING_DISCLOSURE_THRESHOLD;

  const zone4HasAny =
    zone4.incompleteActivitiesCount > 0 ||
    zone4.openSwaEventsCount > 0 ||
    zone4.unresolvedSafetyEventsCount > 0 ||
    (zone4.handoverNotes !== null && zone4.handoverNotes.trim().length > 0);

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        {/* ── Header row ─────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Shift Brief</h2>
            <p className="text-xs text-muted-foreground">
              Snapshot at {format(new Date(generatedAt), 'hh:mm a')}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={handleRefresh}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* ── ZONE 1 — Today's Context ────────────────────────── */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zone 1 &mdash; Today's Context
          </h3>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-medium">{zone1.projectName}</span>
            {zone1.locationName && (
              <>
                <span className="text-muted-foreground">&middot;</span>
                <span>{zone1.locationName}</span>
              </>
            )}
            <span className="text-muted-foreground">&middot;</span>
            <Badge variant="outline" className="capitalize">
              {zone1.shift} shift
            </Badge>
            <span className="text-muted-foreground">
              {format(new Date(zone1.date + 'T00:00:00'), 'EEE, MMM d')}
            </span>
          </div>

          {zone1.ssrName && (
            <p className="text-sm text-muted-foreground">
              SSR: <span className="text-foreground">{zone1.ssrName}</span>
            </p>
          )}

          {/* Weather */}
          {zone1.weather ? (
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium uppercase text-muted-foreground">
                  Weather
                </span>
                {zone1.severeWeatherFlag && (
                  <Badge className="bg-mozaic-red/15 text-mozaic-red">
                    Severe weather flag
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                {zone1.weather.conditionText && (
                  <>
                    <span className="text-muted-foreground">Conditions</span>
                    <span className="text-right">{zone1.weather.conditionText}</span>
                  </>
                )}
                {zone1.weather.temperatureF !== null && (
                  <>
                    <span className="text-muted-foreground">Temperature</span>
                    <span className="text-right font-mono">
                      {zone1.weather.temperatureF}&deg;F
                    </span>
                  </>
                )}
                {zone1.weather.windSpeedKnots !== null && (
                  <>
                    <span className="text-muted-foreground">Wind</span>
                    <span className="text-right font-mono">
                      {zone1.weather.windSpeedKnots} kt
                      {zone1.weather.windDirection ? ` ${zone1.weather.windDirection}` : ''}
                    </span>
                  </>
                )}
                {zone1.weather.visibilityNm !== null && (
                  <>
                    <span className="text-muted-foreground">Visibility</span>
                    <span className="text-right font-mono">
                      {zone1.weather.visibilityNm} nm
                    </span>
                  </>
                )}
                {zone1.weather.seaStateBeaufort !== null && (
                  <>
                    <span className="text-muted-foreground">Sea state</span>
                    <span className="text-right font-mono">
                      Beaufort {zone1.weather.seaStateBeaufort}
                    </span>
                  </>
                )}
              </div>
              {zone1.weather.source && zone1.weather.fetchedAt && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {zone1.weather.source === 'auto' ? 'Auto' : 'Manual'} &middot;{' '}
                  fetched {format(new Date(zone1.weather.fetchedAt), 'MMM d, HH:mm')}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              Weather not logged for this shift.
            </p>
          )}

          {/* Sunrise / sunset — UTC from the solar algorithm; UI may
              localize once project timezone is known. */}
          {zone1.sunrise && zone1.sunset && (
            <p className="text-sm text-muted-foreground">
              Sunrise {zone1.sunrise.substring(11, 16)} UTC &middot; Sunset{' '}
              {zone1.sunset.substring(11, 16)} UTC
            </p>
          )}
        </div>

        {/* ── ZONE 2 — Workforce ──────────────────────────────── */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zone 2 &mdash; Workforce
          </h3>

          {zone2.pobCount !== null ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span>
                <span className="font-medium">POB:</span> {zone2.pobCount}
              </span>
              {zone2.sseCount !== null && (
                <span className="flex items-center gap-1.5">
                  <span>
                    <span className="font-medium">SSE:</span> {zone2.sseCount}
                    {zone2.ssePct !== null && ` (${zone2.ssePct}%)`}
                  </span>
                  {zone2.ssePct !== null && zone2.ssePct > 15 && (
                    <Badge className="bg-mozaic-orange/15 text-mozaic-orange">
                      Elevated
                    </Badge>
                  )}
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              POB manifest not yet published for this shift.
            </p>
          )}

          {/* Supervision coverage proxy (KI-041) */}
          {zone2.supervisionCoverageProxy.totalCrews > 0 && (
            <p className="flex flex-wrap items-center gap-x-1.5 text-sm">
              <span>
                <span className="font-medium">Supervision:</span>{' '}
                {zone2.supervisionCoverageProxy.crewsWithSupervisor}/
                {zone2.supervisionCoverageProxy.totalCrews} crews (
                {zone2.supervisionCoverageProxy.percent}%)
              </span>
              {zone2.supervisionCoverageProxy.percent < 100 && (
                <Badge variant="outline">Gap</Badge>
              )}
            </p>
          )}

          {/* Warning-level hitch flags — always inline */}
          {warningHitchFlags.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Hitch warnings ({warningHitchFlags.length})
              </p>
              <ul className="ml-4 list-disc space-y-0.5 text-sm">
                {warningHitchFlags.map((h, i) => (
                  <li key={i}>
                    {h.personName} &mdash; {h.hitchDays} days
                    <Badge className="ml-1.5 bg-mozaic-orange/15 text-mozaic-orange">
                      &gt;15 days
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Info-level hitch flags — disclosure only when list is long */}
          {shouldOfferHitchDisclosure && (
            <>
              <button
                type="button"
                onClick={() => setShowAllHitchFlags((v) => !v)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {showAllHitchFlags ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                View all {zone2.hitchFlags.length} crew members
              </button>

              {showAllHitchFlags && (
                <ul className="ml-4 list-disc space-y-0.5 text-sm text-muted-foreground">
                  {zone2.hitchFlags.map((h, i) => (
                    <li key={i}>
                      {h.personName} &mdash; {h.hitchDays} days
                      {h.flag === 'warning' && (
                        <Badge className="ml-1.5 bg-mozaic-orange/15 text-mozaic-orange">
                          &gt;15 days
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {/* ── ZONE 3 — Today's Activity Plan ──────────────────── */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zone 3 &mdash; Today's Activity Plan
          </h3>

          {zone3.forecastedActivities.length > 0 ? (
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                Forecasted activities ({zone3.forecastedActivities.length})
              </p>
              <ul className="ml-4 list-disc space-y-1 text-sm">
                {zone3.forecastedActivities.map((a) => (
                  <li key={a.id}>
                    <span>{a.description}</span>
                    <Badge variant="outline" className="ml-1.5 capitalize">
                      {a.anticipatedRisk} risk
                    </Badge>
                    {a.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="ml-1 text-xs"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              No prior shift on this project &mdash; no forecast to carry forward.
            </p>
          )}

          {Object.keys(zone3.riskTagsSummary).length > 0 && (
            <p className="text-sm">
              <span className="font-medium">Risk tags:</span>{' '}
              {Object.entries(zone3.riskTagsSummary).map(([tag, count], i) => (
                <span key={tag}>
                  {i > 0 && ', '}
                  {tag} ({count})
                </span>
              ))}
            </p>
          )}

          {Object.keys(zone3.activePermitsCount).length > 0 && (
            <p className="text-sm">
              <span className="font-medium">Active permits:</span>{' '}
              {Object.entries(zone3.activePermitsCount).map(([type, count], i) => (
                <span key={type}>
                  {i > 0 && ', '}
                  {type.replace(/_/g, ' ')} ({count})
                </span>
              ))}
            </p>
          )}

          {(zone3.sifFlag || zone3.simopsFlag) && (
            <div className="flex flex-wrap gap-2">
              {zone3.sifFlag && (
                <Badge className="bg-mozaic-red/15 text-mozaic-red">
                  SIF forecast
                </Badge>
              )}
              {zone3.simopsFlag && (
                <Badge className="bg-mozaic-orange/15 text-mozaic-orange">
                  SIMOPs detected
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* ── ZONE 4 — Carry-Over From Prior Shift ────────────── */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zone 4 &mdash; Carry-Over From Prior Shift
          </h3>

          {zone4.lookbackDsrCount === 0 ? (
            <p className="text-sm italic text-muted-foreground">
              No prior DSR on this project/shift &mdash; no carry-over to display.
            </p>
          ) : !zone4HasAny ? (
            <p className="text-sm italic text-muted-foreground">
              No carry-over from last {zone4.lookbackDsrCount}{' '}
              shift{zone4.lookbackDsrCount === 1 ? '' : 's'}.
            </p>
          ) : (
            <>
              {/* Incomplete activities */}
              {zone4.incompleteActivitiesCount > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    Incomplete activities ({zone4.incompleteActivitiesCount})
                  </p>
                  <ul className="ml-4 list-disc space-y-0.5 text-sm">
                    {zone4.incompleteActivities.map((a) => (
                      <li key={a.id}>
                        {a.description}
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          &middot; {format(new Date(a.priorDsrDate + 'T00:00:00'), 'MMM d')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {zone4.incompleteActivitiesCount > zone4.incompleteActivities.length && (
                    <p className="ml-4 mt-0.5 text-xs italic text-muted-foreground">
                      + {zone4.incompleteActivitiesCount - zone4.incompleteActivities.length} more &mdash; view prior DSRs for full list
                    </p>
                  )}
                </div>
              )}

              {/* Open SWA events */}
              {zone4.openSwaEventsCount > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    Open SWA events ({zone4.openSwaEventsCount})
                  </p>
                  <ul className="ml-4 list-disc space-y-0.5 text-sm">
                    {zone4.openSwaEvents.map((e) => (
                      <li key={e.id}>
                        {e.hazardDescription}
                        <Badge
                          className={
                            e.workStatus === 'ongoing'
                              ? 'ml-1.5 bg-mozaic-red/15 text-mozaic-red capitalize'
                              : 'ml-1.5 bg-mozaic-orange/15 text-mozaic-orange capitalize'
                          }
                        >
                          {e.workStatus}
                        </Badge>
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          &middot; {format(new Date(e.priorDsrDate + 'T00:00:00'), 'MMM d')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {zone4.openSwaEventsCount > zone4.openSwaEvents.length && (
                    <p className="ml-4 mt-0.5 text-xs italic text-muted-foreground">
                      + {zone4.openSwaEventsCount - zone4.openSwaEvents.length} more
                    </p>
                  )}
                </div>
              )}

              {/* Safety events — KI-043: over-reports because dsr_safety_events
                  has no closure column. Caveat surfaced inline so SSRs don't
                  read this as "all strictly unresolved." */}
              {zone4.unresolvedSafetyEventsCount > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    Safety events in last {zone4.lookbackDsrCount}{' '}
                    shift{zone4.lookbackDsrCount === 1 ? '' : 's'} ({zone4.unresolvedSafetyEventsCount})
                  </p>
                  <p className="-mt-0.5 mb-1 text-xs italic text-muted-foreground">
                    Closure status not yet tracked &mdash; all events surfaced.
                  </p>
                  <ul className="ml-4 list-disc space-y-0.5 text-sm">
                    {zone4.unresolvedSafetyEvents.map((e) => (
                      <li key={e.id}>
                        {e.eventTitle}
                        <Badge variant="outline" className="ml-1.5 capitalize">
                          {e.eventType.replace(/_/g, ' ')}
                        </Badge>
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          &middot; {format(new Date(e.priorDsrDate + 'T00:00:00'), 'MMM d')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {zone4.unresolvedSafetyEventsCount > zone4.unresolvedSafetyEvents.length && (
                    <p className="ml-4 mt-0.5 text-xs italic text-muted-foreground">
                      + {zone4.unresolvedSafetyEventsCount - zone4.unresolvedSafetyEvents.length} more
                    </p>
                  )}
                </div>
              )}

              {/* Handover notes */}
              {zone4.handoverNotes !== null && zone4.handoverNotes.trim().length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    Handover notes
                  </p>
                  <div className="rounded-md bg-muted/50 px-3 py-2 text-sm italic">
                    &ldquo;{zone4.handoverNotes}&rdquo;
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    From prior shift handover.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── ZONE 5 — Attention Items ────────────────────────── */}
        {/* Items arrive pre-sorted by severity (critical → warning →
            info) from the Edge Function; rendering in array order. */}
        <div className="space-y-2 border-t pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zone 5 &mdash; Attention Items
          </h3>

          {zone5.attentionItems.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">
              No attention items for this shift.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {zone5.attentionItems.map((item) => (
                <li key={item.code} className="flex items-start gap-2 text-sm">
                  <Badge
                    className={`${severityBadgeClass(item.severity)} mt-0.5 shrink-0 uppercase`}
                  >
                    {item.severity}
                  </Badge>
                  <div className="flex-1">
                    <span>{item.message}</span>
                    <Badge variant="outline" className="ml-1.5 text-xs text-muted-foreground">
                      Zone {item.sourceZone}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Signals disclosure ──────────────────────────────── */}
        {signals.length > 0 && (
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setSignalsOpen((v) => !v)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {signalsOpen ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              Briefing notes ({signals.length})
            </button>

            {signalsOpen && (
              <div className="mt-2 rounded-md bg-muted/50 px-3 py-2">
                <ul className="ml-4 list-disc space-y-0.5 text-sm">
                  {signals.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
